import type { BlockRegistry } from "../registry/registry.js";
import { bindingSpecsFor } from "../registry/registry.js";
import { resolveDefaultProps, type BlockDefinition, type InspectorField } from "../registry/block-definition.js";
import type { Theme } from "../types/theme.js";
import { listThemeTokens, mergeTheme } from "../theme/tokens.js";
import { defaultDocumentStyles } from "../theme/default-theme.js";
import type { TemplateAiBlock, TemplateAiContext } from "./types.js";

/**
 * What a model needs to know about this designer, as plain data.
 *
 * Blocks declare their props as TypeScript generics, which do not survive to
 * runtime, so this is assembled from what does: the default props (the shape a
 * new block starts with), the inspector's fields and the binding specs. The
 * inspector's functions — `when`, `optionsFrom`, custom components — are left
 * behind; a static option list is kept, because "a heading's level is h1 to h4"
 * is exactly what a model gets wrong without being told.
 *
 * The output is sorted and contains nothing volatile, so the same registry and
 * theme always describe themselves in the same bytes — which is what lets a
 * provider cache the system prompt built from it.
 */
export function describeDesigner(registry: BlockRegistry, theme?: Partial<Theme>): TemplateAiContext {
  const blocks = registry
    .list()
    .map(describeBlock)
    .sort((a, b) => a.type.localeCompare(b.type));
  const merged = mergeTheme(theme);
  const themeTokens = listThemeTokens(merged)
    .filter((t) => t.group !== "brand")
    .map((t) => ({ token: t.token, value: t.value }))
    .sort((a, b) => a.token.localeCompare(b.token));
  return { blocks, themeTokens, textStyles: Object.keys(defaultDocumentStyles().text).sort() };
}

export function describeBlock(def: BlockDefinition): TemplateAiBlock {
  let defaultProps: Record<string, unknown> = {};
  try {
    defaultProps = resolveDefaultProps(def);
  } catch {
    // A default that cannot be computed outside the designer describes nothing.
  }
  const props = new Set(Object.keys(defaultProps));
  const options: Record<string, Array<string | number | boolean>> = {};
  const walk = (fields: InspectorField[]) => {
    for (const f of fields) {
      props.add(f.prop.split(".")[0]!);
      if ((f.kind === "select" && f.options) || f.kind === "segmented") options[f.prop] = f.options!.map((o) => o.value);
    }
  };
  for (const section of def.inspector ?? []) walk(section.fields);
  // Nested specs as well, named `items[].value`: a key/value list's values and a
  // table's column headers are text with {{ }} tokens, and a model told only that
  // `items` is "list" wrote bare expressions into them — which print as the
  // expression, on somebody's invoice.
  const bindings: TemplateAiBlock["bindings"] = [];
  for (const b of bindingSpecsFor(def)) {
    bindings.push({ prop: b.prop, kind: b.kind });
    props.add(b.prop.split(".")[0]!);
    for (const sub of [...(b.items ?? []), ...(b.scoped ?? [])]) {
      const at = b.items?.includes(sub) ? `${b.prop}[].${sub.prop}` : sub.prop;
      bindings.push({ prop: at, kind: sub.perItem ? `${sub.kind}, per item` : sub.kind });
    }
  }
  const c = def.container;
  if (c?.repeat) for (const p of [c.repeat.collectionProp, c.repeat.aliasProp, c.repeat.keepItemTogetherProp, c.repeat.emptyMessageProp, c.repeat.limitProp]) if (p) props.add(p);
  if (c?.conditional) for (const p of [c.conditional.conditionProp, c.conditional.elseChildrenProp]) if (p) props.add(p);

  const out: TemplateAiBlock = {
    type: def.type,
    name: def.name,
    category: def.category,
    defaultProps,
    props: [...props].sort(),
    bindings
  };
  if (def.description) out.description = def.description;
  if (c) out.accepts = [...c.accepts];
  if (Object.keys(options).length) out.options = Object.fromEntries(Object.entries(options).sort(([a], [b]) => a.localeCompare(b)));
  return out;
}
